import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { getCategoryById, getComicsByCategory } from '@/lib/comics';
import ComicCard from '@/components/base/ComicCard';
import type { Comic } from '@/types/comic';

type SortKey = 'update' | 'hot' | 'rating';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'update', label: '最近更新' },
  { key: 'hot', label: '人气最高' },
  { key: 'rating', label: '评分最高' },
];

function sortComics(list: Comic[], key: SortKey): Comic[] {
  const copy = [...list];
  if (key === 'hot') return copy.sort((a, b) => b.popularity - a.popularity);
  if (key === 'rating') return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
}

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type = '' } = useParams();
  const category = getCategoryById(type);
  const [activeTag, setActiveTag] = useState('全部');
  const [sortKey, setSortKey] = useState<SortKey>('update');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  useSEO({
    title: category ? `${category.name} - 韩漫漫画精选` : '分类 - 韩漫漫画精选',
    description: category
      ? `${category.description} 汇总最新最热的${category.name}题材韩漫，持续更新热门韩国漫画资源。`
      : '按题材浏览各类韩漫漫画资源。',
    keywords: category ? `韩漫,${category.name}韩漫,${category.name}漫画,韩国漫画,漫画推荐` : '韩漫,分类,漫画推荐',
    path: `/category/${type}`,
  });

  const baseList = useMemo(() => getComicsByCategory(type), [type]);

  const filtered = useMemo(() => {
    const byTag = activeTag === '全部' ? baseList : baseList.filter((c) => c.tags.includes(activeTag));
    return sortComics(byTag, sortKey);
  }, [baseList, activeTag, sortKey]);

  const shown = filtered.slice(0, visible);

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </div>
        <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-600">该题材可能已下线或链接有误，去其他分类看看吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const tagList = ['全部', ...category.subTags];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>分类</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800 font-medium">{category.name}</span>
      </nav>

      <div className="mt-5 flex items-start gap-4">
        <span className="w-14 h-14 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <i className={`${category.icon} text-3xl`}></i>
        </span>
        <div className="min-w-0">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">{category.name}漫画</h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl">{category.description}</p>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <button
          type="button"
          onClick={() => setFilterOpen((v) => !v)}
          className="md:hidden w-full flex items-center justify-between text-sm font-label font-medium text-foreground-800 cursor-pointer"
        >
          <span className="flex items-center gap-1.5">
            <i className="ri-filter-3-line text-primary-500"></i>
            筛选与排序
          </span>
          <i className={`ri-arrow-down-s-line text-lg transition-transform ${filterOpen ? 'rotate-180' : ''}`}></i>
        </button>

        <div className={`${filterOpen ? 'block' : 'hidden'} md:block mt-4 md:mt-0`}>
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="flex items-start gap-3">
              <span className="shrink-0 text-sm text-foreground-500 pt-1.5">细分题材</span>
              <div className="flex flex-wrap gap-2">
                {tagList.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      setActiveTag(tag);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                      activeTag === tag
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 md:mt-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm text-foreground-500">排序</span>
            <div className="flex gap-1 p-1 rounded-full bg-background-200/70">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSortKey(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                    sortKey === opt.key
                      ? 'bg-background-50 text-primary-600 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="ml-auto text-sm text-foreground-500">共 {filtered.length} 部作品</span>
          </div>
        </div>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {shown.map((comic) => (
              <ComicCard key={comic.id} comic={comic} showAuthor />
            ))}
          </div>
          {visible < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-6 h-11 rounded-md border border-primary-300 text-primary-600 text-sm font-label font-medium hover:bg-primary-100 transition-colors cursor-pointer"
              >
                <i className="ri-add-circle-line text-lg"></i>
                加载更多
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-10 py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-eye-line text-3xl"></i>
          </div>
          <h2 className="mt-5 font-heading font-bold text-xl text-foreground-950">没有符合条件的作品</h2>
          <p className="mt-2 text-sm text-foreground-600">试试重置筛选条件，或返回首页浏览其他题材。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setActiveTag('全部');
                setSortKey('update');
                setVisible(PAGE_SIZE);
              }}
              className="inline-flex items-center gap-1 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-5 h-10 rounded-md border border-background-300 text-foreground-700 text-sm font-label font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}